import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { AuthShell } from "@/components/layouts/auth-shell"
import { useForm, usePage } from "@inertiajs/react"
import { FormEvent } from "react"
import { t } from "@/lib/i18n"

/**
 * Setting the password from the emailed link.
 *
 * Two password fields and nothing else, which is not only tidiness: browsers
 * and password managers recognise this shape and offer to store or update the
 * credential. Anything else in the form costs that.
 */
export default function UserPasswordsEdit({ token }: { token: string }) {
  const { flash = {} } = usePage<{ flash: { alert?: string; notice?: string } }>().props
  const { data, setData, patch, processing } = useForm({
    password: "",
    password_confirmation: "",
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    patch(`/passwords/${token}`)
  }

  return (
    <AuthShell title={t("ui.auth.titles.user_password_edit")} headline={t("ui.auth.user.headline")} blurb={t("ui.auth.user.blurb")}>
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("ui.react.pages.user.passwords.edit.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("ui.react.pages.user.passwords.edit.description")}
        </p>
      </div>

      <form onSubmit={handleSubmit} data-testid="password-reset-form">
        <div className="grid gap-6">
          {flash.alert && (
            <div
              role="alert"
              data-testid="password-reset-flash-alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
            >
              {flash.alert}
            </div>
          )}
          <div className="grid gap-2">
            <Label htmlFor="password">
              {t("ui.react.pages.user.passwords.edit.password")}
            </Label>
            <Input
              id="password"
              type="password"
              value={data.password}
              onChange={(e) => setData("password", e.target.value)}
              required
              autoFocus
              autoComplete="new-password"
              maxLength={72}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="password_confirmation">
              {t("ui.react.pages.user.passwords.edit.password_confirmation")}
            </Label>
            <Input
              id="password_confirmation"
              type="password"
              value={data.password_confirmation}
              onChange={(e) => setData("password_confirmation", e.target.value)}
              required
              autoComplete="new-password"
              maxLength={72}
            />
          </div>
          <Button
            type="submit"
            className="w-full"
            disabled={processing}
            data-testid="update-password-button"
          >
            {t("ui.react.pages.user.passwords.edit.submit")}
          </Button>
        </div>
      </form>
    </AuthShell>
  )
}
